import { onReady } from '../../shared/utils/helpers';
import { MAPBOX_TOKEN } from '../../shared/utils/variables';
import Base from '../../components/base/base';
import CookieConsent from '../../components/cookie-consent/cookie-consent';
import Header from '../../components/header/header';
import './neighborhood.scss';
import GalleryViewer from '../../components/gallery-viewer/gallery-viewer';
import MobileGalleryViewer from '../../components/mobile-gallery-viewer/mobile-gallery-viewer';
import NeighborhoodMap from '../../components/neighborhood-map/neighborhood-map';
import BuyerIntakeForm from '../../components/buyer-intake-form/buyer-intake-form';
import CollectionData from '../../components/collection-data/collection-data';
import HomesitesPhases from '../../components/homesites-phases/homesites-phases';

interface NeighborhoodPageOptions {
  debug?: boolean;
  [key: string]: any;
}

class NeighborhoodPage extends Base {
  private header: Header;
  private map: NeighborhoodMap | null = null;
  private collectionData: CollectionData | null = null;

  constructor(options: NeighborhoodPageOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);
    this.debug = options.debug || false;

    // Initialize header with same debug setting for consistent logging
    this.header = new Header({ debug: this.debug });

    this.init();
  }

  init(): void {
    this.log('NeighborhoodPage initialized');

    // Cookie consent gate. Webflow holds the Meta Pixel until this grants.
    try {
      new CookieConsent({ debug: this.debug });
      this.log('CookieConsent initialized');
    } catch (e) {
      this.log('CookieConsent init failed or skipped', e);
    }

    try {
      this.collectionData = new CollectionData({ debug: this.debug });
      this.log('CollectionData initialized');
    } catch (e) {
      this.log('CollectionData init failed or skipped', e);
    }

    this.initGalleries();

    // "Explore the Area" map
    try {
      this.map = new NeighborhoodMap({
        debug: this.debug,
        accessToken: MAPBOX_TOKEN,
        amenities: false,
        walkBand: false,
        spotlight: false,
        scrollZoom: false
      });
      this.log('NeighborhoodMap initialized');
    } catch (e) {
      this.log('NeighborhoodMap init failed or skipped', e);
    }

    // The homesites map: the phase pills switch the stacked maps beneath them.
    // Only East Village carries the section today; every other neighborhood
    // page skips this quietly.
    try {
      new HomesitesPhases({ debug: this.debug });
      this.log('HomesitesPhases initialized');
    } catch (e) {
      this.log('HomesitesPhases init failed or skipped', e);
    }

    // The See More button carries this page's neighborhood into the
    // listings filter; the slug is the path's last segment.
    const slug = window.location.pathname.split('/').filter(Boolean).pop();
    if (slug) {
      document
        .querySelectorAll<HTMLAnchorElement>('a[data-see-more-listings]')
        .forEach((link) => {
          link.href = `/listings?neighborhood=${encodeURIComponent(slug)}`;
        });
    }

    // Initialize Buyer Intake Form. The container markup is the listing
    // template's, so the page states its own first-screen copy rather than
    // greeting a neighborhood visitor with "Interested in This Property?".
    // Both containers on the page get the same pair.
    try {
      new BuyerIntakeForm({
        debug: this.debug,
        title: 'Interested in This Neighborhood?',
        subtitle:
          'Provide a few details and we’ll follow up with available homes, lot options, and how to schedule a quick call.'
      });
      this.log('BuyerIntakeForm initialized');
    } catch (e) {
      this.log('BuyerIntakeForm init failed or skipped', e);
    }
  }

  /**
   * Both viewers count the grid's images at construction, and imginject
   * fills the grid after CollectionData resolves — so they wait for it.
   */
  private async initGalleries(): Promise<void> {
    if (this.collectionData) await this.collectionData.ready;

    try {
      new GalleryViewer({
        selector: '#launch-gallery',
        lightboxSelector: '.gallery-lightbox',
        contentSelector: '.gallery-lightbox__content',
        debug: this.debug
      });
    } catch (e) {
      this.log('GalleryViewer init failed or skipped', e);
    }

    // MobileGalleryViewer mounts on small screens only; a desktop viewport
    // keeps the desktop viewer alone.
    try {
      if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        // eslint-disable-next-line no-new
        new MobileGalleryViewer({ selector: '.gallery-lightbox__images', debug: this.debug });
      } else {
        this.log('MobileGalleryViewer skipped: viewport too large');
      }
    } catch (e) {
      this.log('MobileGalleryViewer init failed or skipped', e);
    }
  }
}

onReady(() => {
  // You can enable debug mode by passing { debug: true }
  new NeighborhoodPage({ debug: true });
});

export default NeighborhoodPage;
